// kernel/useEntityKernelState.ts
import { useCallback } from 'react';
import {
  ATOMIC_MERGE_REPLACE_KEYS,
  cleanRecord,
  deepEqual,
  deepMerge,
  isPlainObject,
  isRecord,
  mergeEntityWithDraft,
  normalizeDraftValue,
  readCanonicalEntity,
} from '@/kernel/entityMerge';
import type {
  EntityKernelStateEntry,
  KernelEntityType,
  KernelEntityRuntimeState,
  KernelHandle,
  LeasingEventType,
  ProjectKernelMergedState,
  ProjectRuntimeState,
  useEntityKernelStateReturnType,
} from '#types/types';

interface useEntityKernelStateArgs {
  kernel: React.RefObject<KernelHandle | null>;
}

// The shared fusion, normalized: drafts on this path are rebuilt through `normalizeDraftValue`,
// which walks arrays and plain objects and drops every key whose value is `undefined`, so a draft
// never carries an absent field as a present-but-undefined key into comparison or persistence.
// That cleanup is the only difference with the neutral merge kernelStores.ts runs.
const deepMergeDraft = (base: unknown, patch: unknown): unknown =>
  deepMerge(base, patch, [], normalizeDraftValue);

const pruneConvergedDraft = (
  draftValue: unknown,
  serverValue: unknown,
  path: string[] = []
): { keep: boolean; value?: unknown } => {
  if (draftValue === undefined) return { keep: false };
  const currentKey = path[path.length - 1] ?? '';

  if (isPlainObject(draftValue)) {
    if (ATOMIC_MERGE_REPLACE_KEYS.has(currentKey)) {
      if (deepEqual(draftValue, serverValue)) return { keep: false };
      return { keep: true, value: normalizeDraftValue(draftValue) };
    }

    const serverRecord = isPlainObject(serverValue) ? serverValue : null;
    const next = Object.entries(draftValue).reduce<Record<string, unknown>>((acc, [key, value]) => {
      if (!serverRecord || !Object.prototype.hasOwnProperty.call(serverRecord, key)) {
        const normalized = normalizeDraftValue(value);
        if (normalized !== undefined) acc[key] = normalized;
        return acc;
      }

      const pruned = pruneConvergedDraft(value, serverRecord[key], [...path, key]);
      if (pruned.keep) acc[key] = pruned.value;
      return acc;
    }, {});

    if (Object.keys(next).length === 0) return { keep: false };
    return { keep: true, value: next };
  }

  if (Array.isArray(draftValue)) {
    if (deepEqual(draftValue, serverValue)) return { keep: false };
    return { keep: true, value: draftValue.map((entry) => normalizeDraftValue(entry)) };
  }

  if (deepEqual(draftValue, serverValue)) return { keep: false };
  return { keep: true, value: draftValue };
};

// Membership sets: the client keeps them in stack order, the server answers in database order, so
// only the ids say whether they converged. The order shown comes from each layer's renderOrder.
const MEMBERSHIP_SET_KEYS: Partial<Record<KernelEntityType, ReadonlySet<string>>> = {
  StudioWorkspace: new Set(['layerIds', 'versionIds']),
};

const holdsSameIds = (draftValue: unknown, serverValue: unknown): boolean => {
  if (!Array.isArray(draftValue) || !Array.isArray(serverValue) || draftValue.length !== serverValue.length) return false;
  const serverIds = new Set(serverValue);
  return new Set(draftValue).size === draftValue.length && draftValue.every((id) => serverIds.has(id));
};

// A top-level key an autosave sent with another value survives even when equal to canonical, until
// the server's answer to that send shows in the canonical row: that answer would otherwise erase it.
const pruneConvergedDraftOutsideUnobservedSends = (
  entityType: KernelEntityType,
  draft: Record<string, unknown>,
  serverValue: Record<string, unknown>,
  unobservedValues: Array<Record<string, unknown>>
): Record<string, unknown> => {
  const membershipSetKeys = MEMBERSHIP_SET_KEYS[entityType];
  const comparableDraft = membershipSetKeys
    ? Object.fromEntries(Object.entries(draft).filter(([key, value]) => !(membershipSetKeys.has(key) && holdsSameIds(value, serverValue[key]))))
    : draft;
  const pruned = pruneConvergedDraft(comparableDraft, serverValue);
  const next = pruned.keep && isRecord(pruned.value) ? { ...pruned.value } : {};
  Object.entries(draft).forEach(([key, value]) => {
    const divergesFromSentValue = unobservedValues.some((sent) =>
      Object.prototype.hasOwnProperty.call(sent, key) && !deepEqual(value, sent[key])
    );
    if (divergesFromSentValue) next[key] = value;
  });
  return next;
};

export const useEntityKernelState = ({
  kernel,
}: useEntityKernelStateArgs): useEntityKernelStateReturnType => {
  const readEntityFromInventory = useCallback(
    (entityType: KernelEntityType, entityId: string): Record<string, unknown> | null =>
      readCanonicalEntity(kernel.current, entityType, entityId),
    [kernel]
  );

  const makeEntityKernelStateKey = useCallback(
    (entityType: KernelEntityType, entityId: string) => `${entityType}:${entityId}`,
    []
  );

  const readEntityKernelState = useCallback(
    (entityType: KernelEntityType, entityId: string) => {
      const k = kernel.current;
      if (!k || !entityType || !entityId) return null;
      const key = makeEntityKernelStateKey(entityType, entityId);
      const registry = k.getEntityKernelState();
      const entry = registry[key];
      if (!entry || !isRecord(entry.state)) return null;
      return entry.state;
    },
    [kernel, makeEntityKernelStateKey]
  );

  const writeEntityKernelState = useCallback(
    (
      entityType: KernelEntityType,
      entityId: string,
      state: Record<string, unknown>
    ) => {
      const k = kernel.current;
      if (!k || !entityType || !entityId || !isRecord(state)) return;
      const key = makeEntityKernelStateKey(entityType, entityId);
      const cleaned = cleanRecord(state);
      if (Object.keys(cleaned).length === 0) {
        k.setEntityKernelState((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      // Keep drafts minimal: prune keys already converged with canonical DTO.
      const inventoryEntity = readEntityFromInventory(entityType, entityId);
      const nextState = inventoryEntity
        ? pruneConvergedDraftOutsideUnobservedSends(
          entityType,
          cleaned,
          inventoryEntity,
          k.getUnobservedAutosaveValues(entityType, entityId)
        )
        : cleaned;

      if (Object.keys(nextState).length === 0) {
        k.setEntityKernelState((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      const existingState = readEntityKernelState(entityType, entityId);
      if (existingState && deepEqual(existingState, nextState)) {
        return;
      }

      const nextEntry: EntityKernelStateEntry = {
        entityType,
        entityId,
        state: nextState,
        updatedAt: Date.now(),
      };

      k.setEntityKernelState((prev) => {
        const existing = prev[key];
        if (existing && isRecord(existing.state) && deepEqual(existing.state, nextState)) {
          return prev;
        }
        return {
          ...prev,
          [key]: nextEntry,
        };
      });
    },
    [kernel, makeEntityKernelStateKey, readEntityFromInventory]
  );

  const mergeEntityKernelState = useCallback(
    (
      entityType: KernelEntityType,
      entityId: string,
      patch: Record<string, unknown>
    ) => {
      if (!kernel.current || !entityType || !entityId || !isRecord(patch)) return;
      const prevState = readEntityKernelState(entityType, entityId);
      const mergedState = deepMergeDraft(isRecord(prevState) ? prevState : {}, patch);
      if (!isRecord(mergedState)) return;
      writeEntityKernelState(entityType, entityId, mergedState);
    },
    [kernel, readEntityKernelState, writeEntityKernelState]
  );

  const removeEntityKernelState = useCallback(
    (entityType: KernelEntityType, entityId: string) => {
      const k = kernel.current;
      if (!k || !entityType || !entityId) return;
      const key = makeEntityKernelStateKey(entityType, entityId);
      k.setEntityKernelState((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [kernel, makeEntityKernelStateKey]
  );

  const readEntityKernelMergedState = useCallback(
    (entityType: KernelEntityType, entityId: string) => {
      const draft = readEntityKernelState(entityType, entityId);
      const inventoryEntity = readEntityFromInventory(entityType, entityId);
      if (!draft && !inventoryEntity) return null;
      const merged = mergeEntityWithDraft(inventoryEntity || {}, draft || {}, normalizeDraftValue);
      return isRecord(merged) ? merged : null;
    },
    [readEntityFromInventory, readEntityKernelState]
  );

  const readEntityRuntimeState = useCallback(
    <K extends KernelEntityType>(entityType: K, entityId: string): Partial<KernelEntityRuntimeState<K>> => {
      const mergedState = readEntityKernelMergedState(entityType, entityId);
      if (!isRecord(mergedState) || !isRecord(mergedState.runtime)) {
        return {} as Partial<KernelEntityRuntimeState<K>>;
      }
      return mergedState.runtime as Partial<KernelEntityRuntimeState<K>>;
    },
    [readEntityKernelMergedState]
  );

  const readProjectKernelMergedState = useCallback(
    (projectId: string): ProjectKernelMergedState | null => {
      const state = readEntityKernelMergedState('Project', projectId);
      if (!isRecord(state)) return null;
      return state as ProjectKernelMergedState;
    },
    [readEntityKernelMergedState]
  );

  // Clear a key with undefined: a false written after the entity left the registry resurrects it as an id-less merged row.
  const patchEntityRuntimeState = useCallback(
    <K extends KernelEntityType>(
      entityType: K,
      entityId: string,
      patch: Partial<KernelEntityRuntimeState<K>>
    ) => {
      const normalizedEntityId = typeof entityId === 'string' ? entityId.trim() : '';
      if (!normalizedEntityId || !isRecord(patch) || Object.keys(patch).length === 0) return;

      const currentDraft = readEntityKernelState(entityType, normalizedEntityId);
      const nextDraft = isRecord(currentDraft) ? { ...currentDraft } : {};
      const nextRuntime = isRecord(nextDraft.runtime)
        ? { ...nextDraft.runtime }
        : {};

      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined) {
          delete nextRuntime[key];
          return;
        }
        nextRuntime[key] = value;
      });

      if (Object.keys(nextRuntime).length > 0) {
        nextDraft.runtime = nextRuntime;
      } else {
        delete nextDraft.runtime;
      }

      if (Object.keys(nextDraft).length === 0) {
        removeEntityKernelState(entityType, normalizedEntityId);
        return;
      }

      writeEntityKernelState(entityType, normalizedEntityId, nextDraft);
    },
    [readEntityKernelState, removeEntityKernelState, writeEntityKernelState]
  );

  const readProjectRuntimeState = useCallback(
    (projectId: string): Partial<ProjectRuntimeState> => {
      const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
      if (!normalizedProjectId) return {};
      return readEntityRuntimeState('Project', normalizedProjectId);
    },
    [readEntityRuntimeState]
  );

  const patchProjectRuntimeState = useCallback(
    (projectId: string, patch: Partial<ProjectRuntimeState>) => {
      const normalizedProjectId = typeof projectId === 'string' ? projectId.trim() : '';
      if (!normalizedProjectId || !isRecord(patch) || Object.keys(patch).length === 0) return;
      patchEntityRuntimeState('Project', normalizedProjectId, patch);
    },
    [patchEntityRuntimeState]
  );

  const reconcileEntityKernelState = useCallback(
    (
      entityType: KernelEntityType,
      entityId: string,
      options?: {
        eventType?: LeasingEventType;
      }
    ) => {
      const k = kernel.current;
      if (!k || !entityType || !entityId) return;
      const key = makeEntityKernelStateKey(entityType, entityId);

      if (options?.eventType === 'DELETE') {
        k.setEntityKernelState((prev) => {
          if (!prev[key]) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      const inventoryEntity = readEntityFromInventory(entityType, entityId);
      if (!inventoryEntity) return;
      const unobservedValues = k.getUnobservedAutosaveValues(entityType, entityId);

      k.setEntityKernelState((prev) => {
        const entry = prev[key];
        if (!entry || !isRecord(entry.state)) return prev;

        const nextDraft = pruneConvergedDraftOutsideUnobservedSends(
          entityType,
          cleanRecord(entry.state),
          inventoryEntity,
          unobservedValues
        );
        if (Object.keys(nextDraft).length === 0) {
          const next = { ...prev };
          delete next[key];
          return next;
        }

        if (deepEqual(entry.state, nextDraft)) {
          return prev;
        }

        return {
          ...prev,
          [key]: {
            ...entry,
            state: nextDraft,
            updatedAt: Date.now(),
          },
        };
      });
    },
    [kernel, makeEntityKernelStateKey, readEntityFromInventory]
  );

  return {
    makeEntityKernelStateKey,
    readEntityKernelState,
    readEntityKernelMergedState,
    readEntityRuntimeState,
    readProjectKernelMergedState,
    readProjectRuntimeState,
    writeEntityKernelState,
    mergeEntityKernelState,
    patchEntityRuntimeState,
    patchProjectRuntimeState,
    removeEntityKernelState,
    reconcileEntityKernelState,
  };
};
